import { ChatComposer } from '@/components/chat/ChatComposer';
import type { TaAgentVariant } from '@/contexts/PageContext';
import { useChatScroll } from '@/components/chat/useChatScroll';
import { X, ExternalLink, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { useAssistant } from '@/hooks/useAssistant';
import { useAskAssistant } from '@/hooks/usePartnerships';
import { TaChatMode } from '@/components/talent-acquisition/TaChatMode';
import { useOmlSuggestedQuestions } from '@/components/owned-media/oml-assistant-questions';
import { trpc } from '@/lib/trpc';
import supportIALogo from '@/assets/supportIA.png';
// Solo el Agente TA Metrics tiene avatar propio (Sol 18/09); el resto sigue con supportIA.
import taAgentAvatar from '@/assets/ta-agent-avatar.png';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface PartnerCtx {
  partnerId: string;
  partnerName: string;
  partnerSlug: string;
}

interface PartnerMsg {
  role: 'user' | 'assistant';
  text: string;
  suggestedPath?: string;
  driveLinks?: string[];
}

const SUGGESTIONS = [
  '¿Qué paquetes tienen menor inversión mínima?',
  '¿Dónde están los one-pagers y decks?',
  '¿Cuándo usar cada producto?',
];

/** Respuesta de un asistente de una sola consulta (Partnerships, Owned Media). */
interface SingleShotReply {
  reply: string;
  suggestedPath?: string;
  driveLinks?: string[];
}

/**
 * Chat de UNA consulta por pregunta (sin tools): el backend arma el contexto y
 * responde. Lo usan el Hub de Partnerships y Owned Media (08/10/2026); antes era
 * solo de Partnerships y OML hubiera sido una copia.
 */
function SingleShotChatMode({
  ask,
  pending,
  suggestions,
  placeholder,
  initialQuestion,
  onClose,
}: {
  ask: (question: string, history: { role: 'user' | 'assistant'; content: string }[]) => Promise<SingleShotReply>;
  pending: boolean;
  suggestions: string[];
  placeholder: string;
  initialQuestion?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<PartnerMsg[]>([]);
  const [input, setInput] = useState('');
  const navigate = useNavigate();
  const initialSent = useRef(false);
  const bottomRef = useChatScroll([messages]);

  useEffect(() => {
    if (initialQuestion && !initialSent.current) {
      initialSent.current = true;
      send(initialQuestion);
    }
  }, [initialQuestion]);

  const send = async (question?: string) => {
    const q = (question ?? input).trim();
    if (!q || pending) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);

    const history = messages.map(m => ({ role: m.role, content: m.text }));
    let result: SingleShotReply;
    try {
      result = await ask(q, history);
    } catch (e) {
      // El error se ve en la conversación: antes la pregunta quedaba sin respuesta en silencio.
      setMessages(prev => [...prev, { role: 'assistant', text: `⚠️ ${(e as Error)?.message || t('assistant.assistantchatdrawer.error', 'No pude responder. Probá de nuevo.')}` }]);
      return;
    }

    setMessages(prev => [...prev, {
      role: 'assistant',
      text: result.reply,
      suggestedPath: result.suggestedPath,
      driveLinks: result.driveLinks,
    }]);
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="space-y-2 mt-2">
            <p className="text-xs text-muted-foreground">{t('assistant.assistantchatdrawer.preguntas_frecuentes')}</p>
            {suggestions.map(q => (
              <button
                key={q}
                onClick={() => send(q)}
                className="w-full text-left text-sm px-3 py-2.5 rounded-lg border border-muted-foreground/20 hover:bg-muted transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground'
            }`}>
              {msg.role === 'assistant' ? (
                <div className="[&_p]:mb-1 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_strong]:font-semibold [&_code]:bg-black/10 [&_code]:rounded [&_code]:px-1 [&_code]:text-xs text-sm">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
              )}
              {msg.suggestedPath && (
                <button
                  onClick={() => { navigate(msg.suggestedPath!); onClose(); }}
                  className="mt-2 flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <ArrowRight size={12} /> {t('assistant.assistantchatdrawer.ver_en_el_hub')}
                </button>
              )}
              {msg.driveLinks && msg.driveLinks.length > 0 && (
                <div className="mt-2 space-y-1">
                  {msg.driveLinks.map((url, j) => (
                    <a key={j} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline truncate">
                      <ExternalLink size={11} />
                      {url.length > 45 ? url.slice(0, 45) + '…' : url}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {pending && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-3 py-2 text-sm text-muted-foreground animate-pulse">
              {t('assistant.assistantchatdrawer.thinking', 'Pensando…')}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-t p-4">
        <ChatComposer value={input} onChange={setInput} onSend={() => send()} placeholder={placeholder} disabled={pending} />
      </div>
    </>
  );
}

function PartnershipsChatMode({ partnerCtx, initialQuestion, onClose }: { partnerCtx: PartnerCtx; initialQuestion?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const ask = useAskAssistant();
  return (
    <SingleShotChatMode
      ask={(question, history) => ask.mutateAsync({ question, partnerId: partnerCtx.partnerId, conversationHistory: history })}
      pending={ask.isPending}
      suggestions={SUGGESTIONS}
      placeholder={t('assistant.assistantchatdrawer.preguntame_sobre_el_hub')}
      initialQuestion={initialQuestion}
      onClose={onClose}
    />
  );
}

/** Owned Media: responde sobre las cuentas que la persona ve y el Inicio del apartado (08/10/2026). */
function OmlChatMode({ initialQuestion, onClose }: { initialQuestion?: string; onClose: () => void }) {
  const { t } = useTranslation();
  const ask = trpc.oml.assistant.ask.useMutation();
  const suggestions = useOmlSuggestedQuestions();
  return (
    <SingleShotChatMode
      ask={(question, history) => ask.mutateAsync({ question, history })}
      pending={ask.isPending}
      suggestions={suggestions}
      placeholder={t('oml.assistant.placeholder', 'Preguntame sobre las cuentas, el equipo o el Lab…')}
      initialQuestion={initialQuestion}
      onClose={onClose}
    />
  );
}

interface AssistantChatDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  partnerCtx?: PartnerCtx | null;
  /** People → Talent Acquisition: el drawer es el Agente TA Metrics (mismo mecanismo que Partnerships). */
  taActive?: boolean;
  taScope?: TaAgentVariant;
  /** Owned Media: supportIA responde sobre el apartado. */
  omlActive?: boolean;
  initialQuestion?: string;
}

export function AssistantChatDrawer({ isOpen, onClose, partnerCtx, taActive, taScope = 'metrics', omlActive, initialQuestion }: AssistantChatDrawerProps) {
  const { t } = useTranslation();
  const { messages, isLoading, sendMessage, confirmAction } = useAssistant();
  const isPartnerships = !!partnerCtx;
  const isTa = !isPartnerships && !!taActive;
  const isOml = !isPartnerships && !isTa && !!omlActive;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={onClose} />

      {/* En modo TA es un poco más ancho: las respuestas traen tablas por recruiter/país/tipo */}
      <div className={`fixed right-0 top-0 h-full w-full ${isTa ? 'md:w-[580px]' : 'md:w-[500px]'} bg-white dark:bg-zinc-900 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <div className="flex items-center gap-3">
            <img src={isTa ? taAgentAvatar : supportIALogo} alt={isTa ? t('assistant.assistantchatdrawer.ta_name') : 'supportIA'} className="h-12 w-12 rounded-full object-cover" />
            <div>
              <h2 className="text-xl font-semibold">supportIA</h2>
              <p className="text-sm text-muted-foreground">
                {isPartnerships
                  ? t('assistant.assistantchatdrawer.partnerships_context', { name: partnerCtx.partnerName })
                  : isTa
                    ? t('assistant.assistantchatdrawer.ta_context')
                    : isOml
                      ? t('oml.assistant.context', 'Owned Media · responde con la info del apartado')
                      : t('assistant.powered_by')}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" aria-label={t('common.a11y.close')} onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Body — switchea según el modo */}
        {isPartnerships ? (
          <PartnershipsChatMode
            partnerCtx={partnerCtx}
            initialQuestion={initialQuestion}
            onClose={onClose}
          />
        ) : isTa ? (
          <TaChatMode initialQuestion={initialQuestion} scope={taScope} />
        ) : isOml ? (
          <OmlChatMode initialQuestion={initialQuestion} onClose={onClose} />
        ) : (
          <>
            <div className="flex-1 overflow-hidden">
              <ChatMessageList messages={messages} onConfirmAction={confirmAction} />
            </div>
            <div className="border-t p-4">
              <ChatInput onSendMessage={sendMessage} disabled={isLoading} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
